import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiArrowRight, FiShield, FiFileText, FiVideo, FiArrowDown, FiLogOut } from 'react-icons/fi';
import { authService } from '../auth-config/auth-service';
import { useAuthContext } from '../auth-config';

interface FirstTimeOnboardingModalProps {
  userId: number;
  onComplete: () => void;
}

export const FirstTimeOnboardingModal: React.FC<FirstTimeOnboardingModalProps> = ({ userId, onComplete }) => {
  const navigate = useNavigate();
  const { signOut } = useAuthContext();
  const [step, setStep] = useState<number>(1);
  const [hasScrolledStep1, setHasScrolledStep1] = useState<boolean>(false);
  const [hasScrolledStep2, setHasScrolledStep2] = useState<boolean>(false);
  const [hasScrolledStep3, setHasScrolledStep3] = useState<boolean>(false);

  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };
  
  const [agreeStep1, setAgreeStep1] = useState<boolean>(false);
  const [agreeStep2, setAgreeStep2] = useState<boolean>(false);
  const [agreeStep3, setAgreeStep3] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);

  const containerRef1 = useRef<HTMLDivElement>(null);
  const containerRef2 = useRef<HTMLDivElement>(null);
  const containerRef3 = useRef<HTMLDivElement>(null);

  const handleScroll = (ref: React.RefObject<HTMLDivElement>, setScrolled: (val: boolean) => void) => {
    if (!ref.current) return;
    const { scrollTop, scrollHeight, clientHeight } = ref.current;
    if (scrollHeight - scrollTop <= clientHeight + 20) {
      setScrolled(true);
    }
  };

  const handleFinalSubmit = async () => {
    if (!agreeStep1 || !agreeStep2 || !agreeStep3) {
      toast.error('Please accept all three agreements to continue.');
      return;
    }

    try {
      setLoading(true);
      const token = await authService.getAccessToken();
      const response = await fetch('/api/users/accept-agreements', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          userId: Number(userId),
          studentAgreementAccepted: true,
          privacyPolicyAccepted: true,
          videoDisclaimerAccepted: true
        })
      });

      if (response.ok) {
        toast.success('Agreements confirmed! Welcome to Masti Manch.');
        onComplete();
      } else {
        const err = await response.json();
        throw new Error(err.message || 'Failed to record acceptance.');
      }
    } catch (err: any) {
      toast.error(err.message || 'Error saving agreements. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-md p-4 select-none animate-fadeIn">
      <div className="bg-white rounded-3xl max-w-2xl w-full p-5 sm:p-6 shadow-2xl space-y-4 border border-gray-100 flex flex-col max-h-[95vh] overflow-y-auto">
        
        {/* Step Indicator Header */}
        <div className="flex items-center justify-between border-b pb-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600">First-Time Student Setup</span>
            <h2 className="text-2xl font-extrabold text-slate-800">Compliance & Agreements</h2>
          </div>
          <div className="flex items-center space-x-2 text-xs font-bold text-gray-500">
            <span className={`px-3 py-1 rounded-full ${step === 1 ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>1. Agreement</span>
            <span className={`px-3 py-1 rounded-full ${step === 2 ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>2. Privacy & Terms</span>
            <span className={`px-3 py-1 rounded-full ${step === 3 ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>3. Disclaimer</span>
          </div>
        </div>

        {/* STEP 1: Student Agreement */}
        {step === 1 && (
          <div className="flex-1 flex flex-col space-y-4 min-h-0">
            <div className="flex items-center space-x-3 text-blue-700">
              <FiFileText size={24} />
              <h3 className="text-lg font-bold">Step 1: Student Agreement</h3>
            </div>
            <p className="text-xs text-gray-500 font-medium">Please scroll through and review the full Student Agreement below.</p>
            
            <div 
              ref={containerRef1}
              onScroll={() => handleScroll(containerRef1, setHasScrolledStep1)}
              className="flex-1 overflow-y-auto p-4 bg-slate-50 rounded-2xl border border-gray-200 text-xs text-slate-600 leading-relaxed space-y-3 min-h-[120px]"
            >
              <h4 className="font-bold text-slate-800 text-sm">1. Student Obligations & Conduct</h4>
              <p>Welcome to Masti Manch! By using our platform, learning tools, interactive video lectures, and live sessions, you agree to abide by all student conduct rules and code of honor outlined herein.</p>
              <h4 className="font-bold text-slate-800 text-sm">2. Academic Integrity</h4>
              <p>Students are expected to perform all assessments, quizzes, and homework independently unless explicitly assigned as collaborative team assignments. Impersonation or cheating is strictly prohibited.</p>
              <h4 className="font-bold text-slate-800 text-sm">3. Account Safety</h4>
              <p>Account credentials belong strictly to the registered student and parent. Sharing credentials or redistributing proprietary educational software is prohibited.</p>
              <h4 className="font-bold text-slate-800 text-sm">4. Community Respect</h4>
              <p>Always maintain respectful and encouraging communication with teachers, tutors, and fellow students in interactive live sessions and forums.</p>
              <div className="p-3 bg-blue-50 rounded-xl text-blue-800 font-semibold text-center mt-4">
                *** You have reached the end of the Student Agreement ***
              </div>
            </div>

            {!hasScrolledStep1 && (
              <div className="flex items-center justify-center space-x-2 text-amber-600 text-xs font-bold animate-pulse">
                <FiArrowDown />
                <span>Please scroll to the bottom of the agreement to unlock acceptance.</span>
              </div>
            )}

            <label className={`flex items-center space-x-3 p-3 rounded-xl border transition cursor-pointer ${!hasScrolledStep1 ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:bg-blue-50/50 border-blue-200'}`}>
              <input 
                type="checkbox" 
                disabled={!hasScrolledStep1}
                checked={agreeStep1} 
                onChange={(e) => setAgreeStep1(e.target.checked)}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" 
              />
              <span className="text-xs font-bold text-slate-700">I have read and agree to the Student Agreement</span>
            </label>

            <button
              disabled={!hasScrolledStep1 || !agreeStep1}
              onClick={() => setStep(2)}
              className="w-full py-3.5 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm transition flex items-center justify-center space-x-2 shadow-md disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span>Next: Privacy Policy & Terms of Use</span>
              <FiArrowRight size={16} />
            </button>
          </div>
        )}

        {/* STEP 2: Privacy Policy & Terms of Use */}
        {step === 2 && (
          <div className="flex-1 flex flex-col space-y-4 min-h-0">
            <div className="flex items-center space-x-3 text-purple-700">
              <FiShield size={24} />
              <h3 className="text-lg font-bold">Step 2: Privacy Policy & Terms of Use</h3>
            </div>
            <p className="text-xs text-gray-500 font-medium">Please scroll through and review our Privacy Policy & Terms of Use.</p>
            
            <div 
              ref={containerRef2}
              onScroll={() => handleScroll(containerRef2, setHasScrolledStep2)}
              className="flex-1 overflow-y-auto p-4 bg-slate-50 rounded-2xl border border-gray-200 text-xs text-slate-600 leading-relaxed space-y-4 min-h-[120px]"
            >
              <h4 className="font-bold text-slate-900 text-sm uppercase">INTRODUCTION</h4>
              <p>MASTI MANCH INDIA ("Firm", "Platform", "we", "our" or "us") respects the privacy of its students, parents, teachers, visitors and all users of its educational services. Protecting personal information and maintaining user trust are fundamental principles of our organization. This Privacy Policy explains how we collect, use, process, store, share, retain and protect personal information when individuals access or use our website, mobile applications, learning management systems, AI-powered learning tools, live classes, recorded courses, assessments, support services and other educational offerings.</p>
              <p>This Privacy Policy should be read together with our Terms of Use, Student Agreement, Teacher Agreement, Refund Policy, Child Safety Policy, AI Usage Policy and other policies published by MASTI MANCH INDIA. By accessing or using our Platform, users acknowledge that they have read and understood this Privacy Policy and agree to the processing of personal information as described herein, subject to applicable laws.</p>
              <p>This Privacy Policy has been prepared with due regard to the Digital Personal Data Protection Act, 2023, the Information Technology Act, 2000, applicable rules and regulations issued thereunder, CERT-In Directions, child protection laws and other applicable laws in force in India.</p>

              <h4 className="font-bold text-slate-900 text-sm uppercase">ABOUT MASTI MANCH INDIA</h4>
              <p>MASTI MANCH INDIA is an educational technology platform engaged in providing recorded courses, live classes, workshops, assessments, skill-development programs, AI-assisted learning solutions and related educational services. The Platform serves students, including minors, across multiple educational and professional learning domains such as computer education, artificial intelligence, coding, accounting, taxation, data analytics and other subjects.</p>
              <p>The registered office of MASTI MANCH INDIA is situated at First Floor, 291/F-1, Sector 22, Gandhinagar, Gujarat – 382021, India. The official website of the Platform is www.mastimanch.com.</p>

              <h4 className="font-bold text-slate-900 text-sm uppercase">APPLICABILITY OF THIS PRIVACY POLICY</h4>
              <p>This Privacy Policy applies to all individuals who interact with MASTI MANCH INDIA in any capacity. This includes students enrolled in educational programs, parents and guardians of minor students, teachers and instructors, website visitors, prospective students, scholarship applicants, competition participants, corporate clients, institutional partners, affiliates, consultants, contractors and users of any AI-powered services provided by the Platform.</p>

              <h4 className="font-bold text-slate-900 text-sm uppercase">DEFINITIONS</h4>
              <p><strong>Personal Data:</strong> Means any data relating to an identified or identifiable natural person, including names, email addresses, mobile numbers, residential addresses, educational records, photographs, videos, payment information, account details and other information capable of identifying an individual.</p>
              <p><strong>Child:</strong> Refers to any individual below eighteen (18) years of age. The term "Parent" includes a parent, legal guardian or any person legally authorized to act on behalf of a Child.</p>
              <p><strong>Consent:</strong> Means a free, specific, informed, unconditional and unambiguous indication of the individual's agreement to the processing of personal information for a specified purpose.</p>

              <h4 className="font-bold text-slate-900 text-sm uppercase">INFORMATION WE COLLECT & AUTOMATIC COLLECTION</h4>
              <p>We may collect identity-related information, contact details, academic progress, examination performance, attendance records, school details, and parent/guardian verification details. Additionally, technical information such as IP address, browser type, device identifiers, login history, and engagement metrics may be collected automatically to maintain platform security and enhance learning experiences.</p>

              <h4 className="font-bold text-slate-900 text-sm uppercase">CHILDREN'S PRIVACY & PARENTAL CONSENT</h4>
              <p>MASTI MANCH INDIA provides educational services to minor students under 18. Where required by law, we obtain verifiable parental or guardian consent before collecting or processing a child's personal information, implementing additional safeguards to ensure a safe learning environment.</p>

              <h4 className="font-bold text-slate-900 text-sm uppercase">ARTIFICIAL INTELLIGENCE & AUTOMATED PROCESSING</h4>
              <p>We utilize AI technologies (AI tutors, recommendation engines, adaptive learning tools) to personalize study pathways and evaluate academic progress. AI outputs are supplementary, and students are encouraged to verify important academic concepts independently.</p>

              <h4 className="font-bold text-slate-900 text-sm uppercase">DATA SECURITY & CYBERSECURITY COMPLIANCE</h4>
              <p>We implement technical, administrative, and physical security measures (encryption, access controls, vulnerability scanning) consistent with CERT-In directions and Indian data protection laws to prevent unauthorized disclosure, loss, or misuse of personal data.</p>

              <h4 className="font-bold text-slate-900 text-sm uppercase">GOVERNING LAW & JURISDICTION</h4>
              <p>This Privacy Policy & Terms of Use shall be governed by and construed in accordance with the laws of India. Courts located at Gandhinagar, Gujarat, India shall have exclusive jurisdiction over disputes.</p>

              <div className="p-3 bg-purple-50 rounded-xl text-purple-800 font-semibold text-center mt-4">
                *** You have reached the end of Privacy Policy & Terms of Use ***
              </div>
            </div>

            {!hasScrolledStep2 && (
              <div className="flex items-center justify-center space-x-2 text-amber-600 text-xs font-bold animate-pulse">
                <FiArrowDown />
                <span>Please scroll to the bottom of the policy & terms to unlock acceptance.</span>
              </div>
            )}

            <label className={`flex items-center space-x-3 p-3 rounded-xl border transition cursor-pointer ${!hasScrolledStep2 ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:bg-purple-50/50 border-purple-200'}`}>
              <input 
                type="checkbox" 
                disabled={!hasScrolledStep2}
                checked={agreeStep2} 
                onChange={(e) => setAgreeStep2(e.target.checked)}
                className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500" 
              />
              <span className="text-xs font-bold text-slate-700">I have read and accept the Privacy Policy & Terms of Use</span>
            </label>

            <div className="flex space-x-3">
              <button onClick={() => setStep(1)} className="w-1/3 py-3.5 rounded-2xl border text-gray-600 font-bold text-xs hover:bg-gray-50">Back</button>
              <button
                disabled={!hasScrolledStep2 || !agreeStep2}
                onClick={() => setStep(3)}
                className="flex-1 py-3.5 rounded-2xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm transition flex items-center justify-center space-x-2 shadow-md disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span>Next: Video Disclaimers</span>
                <FiArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Video Disclaimers */}
        {step === 3 && (
          <div className="flex-1 flex flex-col space-y-4 min-h-0">
            <div className="flex items-center space-x-3 text-emerald-700">
              <FiVideo size={24} />
              <h3 className="text-lg font-bold">Step 3: Video Disclaimers & Focus AI</h3>
            </div>
            <p className="text-xs text-gray-500 font-medium">Please scroll through and review the Video Learning Disclaimers.</p>
            
            <div 
              ref={containerRef3}
              onScroll={() => handleScroll(containerRef3, setHasScrolledStep3)}
              className="flex-1 overflow-y-auto p-4 bg-slate-50 rounded-2xl border border-gray-200 text-xs text-slate-600 leading-relaxed space-y-4 min-h-[120px]"
            >
              <h4 className="font-bold text-slate-900 text-sm uppercase">Educational Video & Content Disclaimer</h4>
              <p>This video has been created for educational and informational purposes only to support learning for CBSE/STATE board students.</p>

              <h4 className="font-bold text-slate-900 text-sm uppercase">AI Content Enhancement</h4>
              <p>The content, visuals, animations, narration, and examples in this video may have been generated or enhanced using artificial intelligence (AI) tools. Every effort has been made to ensure the accuracy and educational value of the material; however, viewers are encouraged to verify important information from official CBSE/STATE textbooks, school materials, and qualified educators.</p>

              <h4 className="font-bold text-slate-900 text-sm uppercase">Academic Guidance Notice</h4>
              <p>This content is not intended to replace classroom instruction, professional teaching, academic guidance, or official curriculum resources. The creators assume no responsibility for any errors, omissions, or consequences arising from the use of this content.</p>

              <h4 className="font-bold text-slate-900 text-sm uppercase">Acknowledgment</h4>
              <p>By continuing to watch, you acknowledge that this video is provided solely for educational and informational purposes only.</p>

              <div className="p-3 bg-emerald-50 rounded-xl text-emerald-800 font-semibold text-center mt-4">
                *** You have reached the end of Video Disclaimers ***
              </div>
            </div>

            {!hasScrolledStep3 && (
              <div className="flex items-center justify-center space-x-2 text-amber-600 text-xs font-bold animate-pulse">
                <FiArrowDown />
                <span>Please scroll to the bottom of the video disclaimers to unlock acceptance.</span>
              </div>
            )}

            <label className={`flex items-center space-x-3 p-3 rounded-xl border transition cursor-pointer ${!hasScrolledStep3 ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:bg-emerald-50/50 border-emerald-200'}`}>
              <input 
                type="checkbox" 
                disabled={!hasScrolledStep3}
                checked={agreeStep3} 
                onChange={(e) => setAgreeStep3(e.target.checked)}
                className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500" 
              />
              <span className="text-xs font-bold text-slate-700">I understand and agree to the Video & AI Disclaimers</span>
            </label>

            <div className="flex space-x-3">
              <button onClick={() => setStep(2)} disabled={loading} className="w-1/3 py-3.5 rounded-2xl border text-gray-600 font-bold text-xs hover:bg-gray-50">Back</button>
              <button
                disabled={!hasScrolledStep3 || !agreeStep3 || loading}
                onClick={handleFinalSubmit}
                className="flex-1 py-3.5 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition flex items-center justify-center space-x-2 shadow-md disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                ) : (
                  <>
                    <FiCheck size={18} />
                    <span>Confirm & Launch Platform 🚀</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Logout Link */}
        <div className="flex justify-center border-t pt-4">
          <button 
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="text-xs font-semibold text-slate-500 hover:text-red-600 transition-colors duration-200 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoggingOut ? (
              <>
                <span className="w-3.5 h-3.5 border-2 border-slate-500 border-t-transparent rounded-full animate-spin"></span>
                <span>Logging out...</span>
              </>
            ) : (
              <>
                <FiLogOut size={14} />
                <span>Logout</span>
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};
